// React
import { useCallback, useEffect, useRef, useState } from "react";

// Third-Party
import { Box, Palette, Stack } from "@mui/material";
import { useSnackbar } from "notistack";

// Internal
import { useChatActions } from "../hooks/useChatActions";
import { useChatStream } from "../hooks/useChatStream";
import { PendingUpload } from "../hooks/useFileUpload";
import { AskDostGreeting } from "./AskDostGreeting";
import { AskDostInput } from "./AskDostInput";
import { ChatMessages, ChatMessagesHandle } from "./messages/ChatMessages";

// Types
import { useFormContext } from "react-hook-form";
import { ISendChatMessage } from "../types";

export interface UserInfo {
  firstName?: string;
  [key: string]: any;
}

interface IAskDostContentProps {
  data: ISendChatMessage;
  onMessageChange: (value: string) => void;
  onAttachmentsChange: (attachments: ISendChatMessage["attachments"]) => void;
  onSubmit: () => void;
  isSending: boolean;
  userInfo?: UserInfo;
  palette: Palette;
  chatHistory: any;
  translate: (text: string, options?: any) => string;
  initialPendingUploads?: PendingUpload[];
  onPendingUploadsChange?: (pendingUploads: PendingUpload[]) => void;
}

export const AskDostContent: React.FC<IAskDostContentProps> = (props) => {
  const {
    data,
    onMessageChange,
    onAttachmentsChange,
    onSubmit,
    isSending,
    userInfo,
    palette,
    translate,
    chatHistory,
    initialPendingUploads,
    onPendingUploadsChange,
  } = props;
  const { messages, addMessage, handleStreamEvent } = chatHistory;
  const chatMessagesRef = useRef<ChatMessagesHandle>(null);
  const { setValue } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();

  const [isStreaming, setIsStreaming] = useState(false);

  const handleStreamComplete = useCallback(() => {
    setIsStreaming(false);
    onSubmit();
  }, [onSubmit]);

  const handleStreamError = useCallback(
    (error: Error) => {
      console.error("Stream error:", error);
      setIsStreaming(false);
      if (!navigator.onLine) {
        enqueueSnackbar(translate("offline_error"), { variant: "error" });
      }
    },
    [enqueueSnackbar, translate],
  );

  const { streamChat, cancelStream } = useChatStream({
    onEvent: handleStreamEvent,
    onComplete: handleStreamComplete,
    onError: handleStreamError,
  });

  const { handleSendMessage: sendMessage, handleRegenerate } = useChatActions({
    messages,
    addMessage,
    streamChat,
    translate,
    onStreamStart: () => setIsStreaming(true),
    onStreamComplete: () => {
      setIsStreaming(false);
    },
    onStreamError: () => {
      setIsStreaming(false);
    },
  });

  const handleSendMessage = useCallback(async () => {
    setValue("message", "");
    setValue("attachments", []);
    await sendMessage(data);
    chatMessagesRef.current?.scrollToBottom();
  }, [sendMessage, data, setValue]);

  const handleQuickSend = useCallback(
    async (text: string) => {
      setValue("message", "");
      setValue("attachments", []);
      const quickData: ISendChatMessage = {
        ...data,
        message: text,
        attachments: [],
      };
      await sendMessage(quickData);
      chatMessagesRef.current?.scrollToBottom();
    },
    [data, sendMessage, setValue],
  );

  const handleCancel = useCallback(() => {
    cancelStream();
  }, [cancelStream]);

  useEffect(() => {
    const handleOffline = () => {
      cancelStream();
      setIsStreaming(false);
      enqueueSnackbar(translate("offline_error"), { variant: "error" });
    };
    const handleOnline = () => {
      enqueueSnackbar(translate("back_online"), { variant: "success" });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [cancelStream, enqueueSnackbar, translate]);

  const handleEditInstructions = useCallback(() => {
    if (!navigator.onLine) {
      enqueueSnackbar(translate("offline_error"), { variant: "error" });
      return;
    }
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.sender === "user");
    if (lastUserMessage) {
      setValue("message", lastUserMessage.content);
    }
  }, [messages, setValue, enqueueSnackbar, translate]);

  const hasMessages = messages.length > 0;

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {hasMessages ? (
        <Stack
          spacing={2}
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <ChatMessages
            ref={chatMessagesRef}
            messages={messages}
            palette={palette}
            translate={translate}
            onRegenerate={handleRegenerate}
            onEditInstructions={handleEditInstructions}
            onSendMessage={handleQuickSend}
            isStreaming={isStreaming}
          />

          <AskDostInput
            value={data.message}
            onChange={onMessageChange}
            attachments={data.attachments}
            onAttachmentsChange={onAttachmentsChange}
            palette={palette}
            onSubmit={handleSendMessage}
            isSending={isStreaming}
            translate={translate}
            onCancel={handleCancel}
            showRotatingGlow={!hasMessages}
            initialPendingUploads={initialPendingUploads}
            onPendingUploadsChange={onPendingUploadsChange}
          />
        </Stack>
      ) : (
        <Stack
          direction="column"
          spacing={2}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <AskDostGreeting
            firstName={userInfo?.firstName}
            palette={palette}
            translate={translate}
          />
          <AskDostInput
            value={data.message}
            onChange={onMessageChange}
            attachments={data.attachments}
            onAttachmentsChange={onAttachmentsChange}
            onSubmit={handleSendMessage}
            isSending={isStreaming}
            palette={palette}
            translate={translate}
            onCancel={handleCancel}
            showRotatingGlow={!hasMessages}
            initialPendingUploads={initialPendingUploads}
            onPendingUploadsChange={onPendingUploadsChange}
          />
        </Stack>
      )}
    </Box>
  );
};
